// Family card — Phase 1c of the revised EMR framework (2026-10-03).
//
// Family flow differs from the single-patient cards because a family card
// has two entities: the family account (patient_families) and its
// beneficiaries (family_beneficiaries → patients). The receptionist either
// finds an existing family or creates one, then opens the family detail
// screen to manage beneficiaries.
//
// Shape:
//   1. Search bar matches family_name / account_number / main_contact_phone.
//      Strong match (exact account_number or main_contact_phone) surfaces a
//      "possible duplicate" banner.
//   2. Create form captures: family_name (required) + optional
//      account_number, address, main_contact_name, main_contact_phone,
//      main_contact_email.
//   3. On create, navigates to /reception/family/:id for beneficiary
//      management.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ReceptionFamilyHit } from '@pamosi/shared';
import { ApiError, receptionApi } from '../lib/api';

const PHONE_RE = /^\+[1-9]\d{7,14}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function FamilyCardScreen() {
    const navigate = useNavigate();

    const [query, setQuery] = useState('');
    const [hits, setHits] = useState<ReceptionFamilyHit[] | null>(null);
    const [strongMatch, setStrongMatch] = useState(false);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);

    const [familyName, setFamilyName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [address, setAddress] = useState('');
    const [mainContactName, setMainContactName] = useState('');
    const [mainContactPhone, setMainContactPhone] = useState('');
    const [mainContactEmail, setMainContactEmail] = useState('');
    const [overrideDuplicate, setOverrideDuplicate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    const blocksCreate = useMemo(
        () => strongMatch && hits && hits.length > 0 && !overrideDuplicate,
        [strongMatch, hits, overrideDuplicate],
    );

    async function search() {
        const q = query.trim();
        if (q.length < 2) { setSearchError('Type at least 2 characters.'); return; }
        setSearchError(null);
        setSearching(true);
        try {
            const res = await receptionApi.searchFamilies(q);
            setHits(res.hits);
            setStrongMatch(res.has_strong_match);
            setOverrideDuplicate(false);
            if (!familyName && !PHONE_RE.test(q)) setFamilyName(q);
            if (PHONE_RE.test(q)) setMainContactPhone(q);
        } catch (err) {
            setSearchError(err instanceof ApiError ? err.message || err.code : 'Search failed.');
            setHits(null);
            setStrongMatch(false);
        } finally {
            setSearching(false);
        }
    }

    async function createFamilyRec() {
        setCreateError(null);
        if (familyName.trim().length === 0) {
            setCreateError('Family name is required.'); return;
        }
        if (mainContactPhone && !PHONE_RE.test(mainContactPhone)) {
            setCreateError('Main contact phone must be E.164 (e.g. +2348012345678).'); return;
        }
        if (mainContactEmail && !EMAIL_RE.test(mainContactEmail)) {
            setCreateError('Main contact email looks invalid.'); return;
        }
        setCreating(true);
        try {
            const res = await receptionApi.createFamily({
                familyName: familyName.trim(),
                ...(accountNumber.trim() ? { accountNumber: accountNumber.trim() } : {}),
                ...(address.trim() ? { address: address.trim() } : {}),
                ...(mainContactName.trim() ? { mainContactName: mainContactName.trim() } : {}),
                ...(mainContactPhone.trim() ? { mainContactPhoneE164: mainContactPhone.trim() } : {}),
                ...(mainContactEmail.trim() ? { mainContactEmail: mainContactEmail.trim() } : {}),
            });
            navigate(`/reception/family/${res.family.id}`);
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.code === 'account_number_taken') setCreateError('That account number is already in use at this clinic.');
                else if (err.code === 'insufficient_role') setCreateError('Your role can\'t create families. Ask reception or an admin.');
                else setCreateError(err.message || err.code);
            } else setCreateError('Create failed. Try again.');
        } finally {
            setCreating(false);
        }
    }

    return (
        <div className="space-y-6">
            <div>
                <button type="button"
                    className="text-slate-500 text-sm hover:text-slate-700"
                    onClick={() => navigate('/reception')}
                >← Back to Reception</button>
                <h1 className="text-slate-900 text-2xl font-semibold mt-2">Family card</h1>
                <p className="text-slate-500 text-sm mt-1">
                    Shared account for billing + contact; each beneficiary keeps their own medical record.
                </p>
            </div>

            {/* Search */}
            <div className="pm-card p-4 space-y-3">
                <div className="text-slate-700 text-sm font-semibold">Find existing family</div>
                <div className="flex gap-2">
                    <input type="search"
                        className="pm-input flex-1"
                        placeholder="Family name, account number, or main contact phone"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
                    />
                    <button type="button"
                        className="pm-btn-outline whitespace-nowrap"
                        disabled={searching}
                        onClick={() => void search()}
                    >{searching ? 'Searching…' : 'Find'}</button>
                </div>
                {searchError && <div className="text-rose-700 text-xs">{searchError}</div>}

                {hits && hits.length === 0 && !searching && (
                    <div className="text-slate-500 text-sm">
                        No families match at this clinic. Create a new one below.
                    </div>
                )}
                {hits && hits.length > 0 && (
                    <div className="space-y-2">
                        <div className="text-slate-500 text-xs uppercase tracking-wide">
                            {hits.length} match{hits.length === 1 ? '' : 'es'}
                        </div>
                        {strongMatch && !overrideDuplicate && (
                            <div className="pm-card p-3 border-amber-300 bg-amber-50">
                                <div className="text-amber-900 text-sm">
                                    <span className="font-semibold">Possible duplicate.</span>{' '}
                                    One of these looks like the same family. Open it or press <em>Create anyway</em>.
                                </div>
                            </div>
                        )}
                        <ul className="space-y-2">
                            {hits.map((h) => (
                                <li key={h.family_id}>
                                    <button type="button"
                                        onClick={() => navigate(`/reception/family/${h.family_id}`)}
                                        className="pm-card p-3 w-full text-left hover:border-slate-300 transition"
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="text-slate-900 font-medium truncate">{h.family_name}</div>
                                                <div className="text-slate-500 text-xs mt-0.5">
                                                    {h.main_contact_phone_e164 ?? '(no main phone)'}
                                                    {' · '}
                                                    {h.beneficiary_count} beneficiar{h.beneficiary_count === 1 ? 'y' : 'ies'}
                                                </div>
                                            </div>
                                            {h.account_number && (
                                                <div className="text-slate-700 text-xs font-mono">#{h.account_number}</div>
                                            )}
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Create */}
            <div className="pm-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="text-slate-700 text-sm font-semibold">Create new family</div>
                    {blocksCreate && (
                        <button type="button" className="text-amber-700 text-xs underline"
                            onClick={() => setOverrideDuplicate(true)}
                        >Create anyway</button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block md:col-span-2">
                        <span className="text-slate-500 text-xs">Family name</span>
                        <input className="pm-input mt-1" placeholder="e.g. Adeyemi"
                            value={familyName} onChange={(e) => setFamilyName(e.target.value)} disabled={creating} />
                    </label>
                    <label className="block">
                        <span className="text-slate-500 text-xs">Account number <span className="text-slate-400">(optional, must be unique)</span></span>
                        <input className="pm-input mt-1 font-mono" placeholder="e.g. FM-00123"
                            value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} disabled={creating} />
                    </label>
                    <label className="block">
                        <span className="text-slate-500 text-xs">Address <span className="text-slate-400">(optional)</span></span>
                        <input className="pm-input mt-1" placeholder="Street, city"
                            value={address} onChange={(e) => setAddress(e.target.value)} disabled={creating} />
                    </label>
                    <label className="block">
                        <span className="text-slate-500 text-xs">Main contact name <span className="text-slate-400">(optional)</span></span>
                        <input className="pm-input mt-1" placeholder="Account holder name"
                            value={mainContactName} onChange={(e) => setMainContactName(e.target.value)} disabled={creating} />
                    </label>
                    <label className="block">
                        <span className="text-slate-500 text-xs">Main contact phone <span className="text-slate-400">(optional, E.164)</span></span>
                        <input type="tel" className="pm-input mt-1" placeholder="+2348012345678"
                            value={mainContactPhone} onChange={(e) => setMainContactPhone(e.target.value.trim())} disabled={creating} />
                    </label>
                    <label className="block md:col-span-2">
                        <span className="text-slate-500 text-xs">Main contact email <span className="text-slate-400">(optional)</span></span>
                        <input type="email" className="pm-input mt-1" placeholder="name@example.com"
                            value={mainContactEmail} onChange={(e) => setMainContactEmail(e.target.value.trim())} disabled={creating} />
                    </label>
                </div>

                {createError && <div className="text-rose-700 text-xs">{createError}</div>}

                <button type="button"
                    className="pm-btn-primary w-full"
                    disabled={creating || Boolean(blocksCreate)}
                    onClick={() => void createFamilyRec()}
                >{creating ? 'Creating…' : 'Create family card'}</button>
                {blocksCreate && (
                    <p className="text-slate-400 text-xs text-center">
                        A possible duplicate is in the list above. Open it, or press <em>Create anyway</em>.
                    </p>
                )}
            </div>
        </div>
    );
}
